/**
 * fieldGeojson — AK-321 v1.1.
 *
 * Источник геометрии для (1) snap к границам полей и (2) «обвести поле».
 * Полная геометрия поля берётся из `GET /api/v1/fields/{id}/geojson`
 * (НЕ MVT — MVT квантует координаты до тайл-сетки и непригоден для
 * прилипания/preload вершин).
 *
 * Кэш по field_id живёт в module-scope (TTL = сессия вкладки). Поля не меняют
 * геометрию между визитами на /map, поэтому кэшировать на сессию безопасно и
 * убирает повторные round-trip'ы при наведении/обводке.
 *
 * Чистый сетевой/кэш-слой — без MapLibre/DOM, удобно тестировать.
 */
import { api } from '@/lib/api';

/** [lng, lat] — порядок MapLibre / GeoJSON. */
export type LngLat = [number, number];

/** Ответ backend: GeoJSON Feature с Polygon | MultiPolygon. */
interface FieldGeojsonFeature {
  type: 'Feature';
  id?: string;
  properties?: { name?: string } | null;
  geometry:
    | { type: 'Polygon'; coordinates: number[][][] }
    | { type: 'MultiPolygon'; coordinates: number[][][][] };
}

/**
 * Нормализованная геометрия поля: внешние кольца всех частей (для snap)
 * + первое (крупнейшее) внешнее кольцо как editable-кандидат для «обвести»
 * + ПОЛНАЯ мультиполигон-структура (части + дырки) для загрузки в редактор
 * (AK-371 — не теряем дырки/несмежные части при «Обвести поле»).
 */
export interface FieldGeometry {
  fieldId: string;
  name: string;
  /** Все внешние кольца (по части мультиполигона), для snap-hit-test. */
  outerRings: LngLat[][];
  /** Внешнее кольцо крупнейшей части — кандидат для editable feature. */
  primaryRing: LngLat[];
  /**
   * AK-371: полная геометрия как MultiPolygon-координаты
   * (parts[part][ring][pt]; ring 0 — внешнее, ring 1+ — дырки). Сохраняет
   * несмежные части и внутренние кольца при загрузке в редактор.
   */
  multiPolygon: LngLat[][][];
  /** bbox [minLng, minLat, maxLng, maxLat] (для viewport-фильтра кандидатов). */
  bbox: [number, number, number, number];
}

const cache = new Map<string, FieldGeometry>();
const inflight = new Map<string, Promise<FieldGeometry>>();

/** Сбросить кэш (на смену тенанта/сезона — вызывается извне при необходимости). */
export function clearFieldGeometryCache(): void {
  cache.clear();
  inflight.clear();
}

/** Достать из кэша без запроса (для синхронного snap-hit-test). */
export function getCachedFieldGeometry(fieldId: string): FieldGeometry | undefined {
  return cache.get(fieldId);
}

/**
 * Загрузить (или взять из кэша) геометрию поля. Дедуп параллельных запросов
 * по field_id через inflight-map.
 */
export async function loadFieldGeometry(fieldId: string): Promise<FieldGeometry> {
  const cached = cache.get(fieldId);
  if (cached) return cached;
  const pending = inflight.get(fieldId);
  if (pending) return pending;

  const p = (async () => {
    const feat = await api.get<FieldGeojsonFeature>(`/api/v1/fields/${fieldId}/geojson`);
    const geom = normalize(fieldId, feat);
    cache.set(fieldId, geom);
    inflight.delete(fieldId);
    return geom;
  })().catch((err) => {
    inflight.delete(fieldId);
    throw err;
  });
  inflight.set(fieldId, p);
  return p;
}

/** Превращает GeoJSON Feature в FieldGeometry (внешние кольца + части + bbox). */
function normalize(fieldId: string, feat: FieldGeojsonFeature): FieldGeometry {
  const outerRings: LngLat[][] = [];
  // AK-371: собираем ПОЛНУЮ мультиполигон-структуру (части + дырки), не только
  // внешние кольца — чтобы «Обвести поле» загружало донаты/несмежные части.
  const multiPolygon: LngLat[][][] = [];
  if (feat.geometry.type === 'Polygon') {
    const rings = feat.geometry.coordinates.map(toRing);
    if (rings[0]) {
      outerRings.push(rings[0]);
      multiPolygon.push(rings);
    }
  } else {
    for (const part of feat.geometry.coordinates) {
      const rings = part.map(toRing);
      if (rings[0]) {
        outerRings.push(rings[0]);
        multiPolygon.push(rings);
      }
    }
  }
  // Крупнейшее кольцо (по числу точек — дёшево и достаточно для выбора части).
  let primaryRing: LngLat[] = [];
  for (const ring of outerRings) {
    if (ring.length > primaryRing.length) primaryRing = ring;
  }
  return {
    fieldId,
    name: feat.properties?.name ?? '',
    outerRings,
    primaryRing,
    multiPolygon,
    bbox: computeBbox(outerRings),
  };
}

function toRing(coords: number[][]): LngLat[] {
  return coords.map((c) => [c[0], c[1]] as LngLat);
}

function computeBbox(rings: LngLat[][]): [number, number, number, number] {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return [minLng, minLat, maxLng, maxLat];
}
